import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, Download, Sparkles, CheckCircle2, AlertTriangle, Image as ImageIcon } from 'lucide-react';
import api from '@/services/apiClient';
import PageHeader from '@/components/ui/PageHeader';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Card from '@/components/ui/Card';
import Spinner from '@/components/ui/Spinner';
import EmptyState from '@/components/ui/EmptyState';
import styles from './ExerciseImport.module.css';

const keyOf = (c) => c.externalId || c.name;
const PAGE_SIZE = 24;

export default function ExerciseImport() {
  const { t } = useTranslation();
  const [q, setQ] = useState('');
  const [source, setSource] = useState(null); // null = backend escolhe
  const [info, setInfo] = useState(null); // { enabled, source, version, sources }
  const [candidates, setCandidates] = useState([]);
  const [selected, setSelected] = useState(() => new Set());
  const [enrich, setEnrich] = useState(true);
  const [total, setTotal] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [job, setJob] = useState(null); // job de importação em andamento/finalizado
  const [jobKeys, setJobKeys] = useState(null); // chaves enviadas (null = importar tudo)
  const [hideImported, setHideImported] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [history, setHistory] = useState([]);

  const importing = job?.status === 'running';

  const loadHistory = () => {
    api
      .get('/admin/exercises/history')
      .then((r) => setHistory((r.data || r).logs || []))
      .catch(() => {});
  };

  const fetchPage = async (src, offset) => {
    const res = await api.get('/admin/exercises/search', {
      q,
      limit: PAGE_SIZE,
      offset,
      ...(src ? { source: src } : {}),
    });
    return res.data || res;
  };

  // hasMore: usa o total quando a fonte informa; senão, página cheia = deve ter mais.
  const computeHasMore = (shown, batch, totalCount) =>
    totalCount != null ? shown < totalCount : batch.length === PAGE_SIZE;

  const search = async (srcOverride) => {
    setLoading(true);
    setError('');
    setResult(null);
    try {
      const src = srcOverride !== undefined ? srcOverride : source;
      const data = await fetchPage(src, 0);
      const batch = data.candidates || [];
      setInfo({ enabled: data.enabled, source: data.source, version: data.version, sources: data.sources });
      setCandidates(batch);
      setTotal(data.total ?? null);
      setHasMore(computeHasMore(batch.length, batch, data.total ?? null));
      setSelected(new Set());
    } catch (e) {
      setError(e.message || t('import.errSearchFailed'));
    } finally {
      setLoading(false);
    }
  };

  // Carrega a próxima página e acrescenta ao grid (mantém a seleção atual).
  const loadMore = async () => {
    setLoadingMore(true);
    setError('');
    try {
      const data = await fetchPage(source, candidates.length);
      const seen = new Set(candidates.map(keyOf));
      const batch = (data.candidates || []).filter((c) => !seen.has(keyOf(c)));
      const next = [...candidates, ...batch];
      setCandidates(next);
      setTotal(data.total ?? total);
      setHasMore(computeHasMore(next.length, data.candidates || [], data.total ?? total));
    } catch (e) {
      setError(e.message || t('import.errSearchFailed'));
    } finally {
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    search();
    loadHistory();
    // Se houver importação rodando (ex.: reload da página), retoma o acompanhamento.
    api
      .get('/admin/exercises/import/jobs/active')
      .then((r) => {
        const active = (r.data || r).job;
        if (active?.status === 'running') setJob(active);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Polling do job em andamento; ao terminar, atualiza resultado/flags/histórico.
  useEffect(() => {
    if (!importing) return;
    const timer = setInterval(async () => {
      try {
        const r = await api.get(`/admin/exercises/import/jobs/${job.id}`);
        const next = (r.data || r).job;
        setJob(next);
        if (next.status !== 'running') {
          setResult(next);
          loadHistory();
          if (jobKeys) {
            // Marca localmente os itens processados como importados.
            setCandidates((list) =>
              list.map((c) => (jobKeys.has(keyOf(c)) ? { ...c, imported: true } : c))
            );
          } else {
            search(); // importar tudo: recarrega as flags do servidor
          }
        }
      } catch {
        // mantém o polling; erro transitório
      }
    }, 1500);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job?.id, importing]);

  const toggle = (c) => {
    const k = keyOf(c);
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });
  };
  const visible = useMemo(
    () => (hideImported ? candidates.filter((c) => !c.imported) : candidates),
    [candidates, hideImported]
  );
  const allSelected = visible.length > 0 && selected.size === visible.length;
  const toggleAll = () =>
    setSelected(allSelected ? new Set() : new Set(visible.map(keyOf)));

  const selectedCandidates = useMemo(
    () => candidates.filter((c) => selected.has(keyOf(c))),
    [candidates, selected]
  );

  const doImport = async () => {
    if (!selectedCandidates.length || importing) return;
    setError('');
    setResult(null);
    try {
      const res = await api.post('/admin/exercises/import', {
        candidates: selectedCandidates,
        enrich,
        query: q,
      });
      setJobKeys(new Set(selectedCandidates.map(keyOf)));
      setJob((res.data || res).job);
      setSelected(new Set());
    } catch (e) {
      setError(e.message || t('import.errImportFailed'));
    }
  };

  // Importa toda a busca atual (o servidor pagina a fonte e processa em background).
  const doImportAll = async () => {
    if (importing) return;
    if (!confirm(t('import.importAllConfirm'))) return;
    setError('');
    setResult(null);
    try {
      const res = await api.post('/admin/exercises/import', {
        all: true,
        enrich,
        query: q,
        ...(source ? { source } : {}),
      });
      setJobKeys(null);
      setJob((res.data || res).job);
      setSelected(new Set());
    } catch (e) {
      setError(e.message || t('import.errImportFailed'));
    }
  };

  return (
    <>
      <PageHeader kicker="Movez · Catálogo" title={t('import.pageTitle')} />

      {/* Aviso quando a fonte não está configurada */}
      {info && !info.enabled && (
        <Card className={styles.notice}>
          <AlertTriangle size={18} strokeWidth={2} />
          <div>
            <strong>{t('import.noticeTitle')}</strong>
            <p>
              {t('import.noticeBefore')} <code>EXERCISEDB_API_KEY</code> {t('import.noticeMiddle')}{' '}
              <code>{info.version}</code>. {t('import.noticeAfter')} <code>OPENAI_API_KEY</code>.
            </p>
          </div>
        </Card>
      )}

      {/* Seletor de fonte */}
      {info?.sources?.length > 0 && (
        <div className={styles.sources}>
          {info.sources.map((s) => {
            const activeId = source || (info.source?.startsWith('exercisedb') ? 'exercisedb' : 'free-exercise-db');
            return (
              <button
                key={s.id}
                type="button"
                className={`${styles.sourceBtn} ${activeId === s.id ? styles.sourceOn : ''}`}
                disabled={!s.enabled}
                onClick={() => {
                  setSource(s.id);
                  search(s.id);
                }}
                title={s.enabled ? '' : t('import.sourceDisabledHint')}
              >
                {s.label}
                {!s.enabled && ` ${t('import.sourceNoKey')}`}
              </button>
            );
          })}
          <span className={styles.langNote}>{t('import.langNote')}</span>
        </div>
      )}

      {/* Busca */}
      <div className={styles.toolbar}>
        <div className={styles.searchBox}>
          <Search size={16} strokeWidth={2} />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && search()}
            placeholder={t('import.searchPlaceholder')}
          />
        </div>
        <Button onClick={search} loading={loading}>
          {t('common.search')}
        </Button>
        <button
          type="button"
          className={`${styles.enrich} ${enrich ? styles.enrichOn : ''}`}
          onClick={() => setEnrich((v) => !v)}
          title={t('import.enrichHint')}
        >
          <Sparkles size={15} strokeWidth={2} />
          {t('import.aiLabel')} {enrich ? t('import.aiOn') : t('import.aiOff')}
        </button>
      </div>

      {error && <div className={styles.error}>{error}</div>}

      {/* Progresso da importação em background */}
      {importing && (
        <Card className={styles.progressCard}>
          <div className={styles.progressHead}>
            <Spinner size={16} />
            <strong>{t('import.progressTitle')}</strong>
            <span className={styles.progressNums}>
              {t('import.progressCount', { processed: job.processed, total: job.total })}
            </span>
          </div>
          <div className={styles.progressTrack}>
            <div
              className={styles.progressFill}
              style={{ width: `${job.total ? Math.round((job.processed / job.total) * 100) : 0}%` }}
            />
          </div>
          <div className={styles.progressMeta}>
            {t('import.histCreated', { count: job.created })} · {t('import.histUpdated', { count: job.updated })} ·{' '}
            {t('import.histEnriched', { count: job.enriched })}
            {job.failed ? ` · ${t('import.histFailed', { count: job.failed })}` : ''}
          </div>
        </Card>
      )}

      {result && !importing && (
        <div className={styles.result}>
          <CheckCircle2 size={16} strokeWidth={2} />
          {t('import.resultPrefix')} <strong>{result.created}</strong> {t('import.resultCreated')} · <strong>{result.updated}</strong>{' '}
          {t('import.resultUpdated')} · <strong>{result.enriched}</strong> {t('import.resultEnriched')} ·{' '}
          <strong>{result.translated ?? 0}</strong> {t('import.resultTranslated')}
          {result.failed ? ` · ${t('import.resultFailed', { count: result.failed })}` : ''}
        </div>
      )}

      {/* Resultados */}
      {loading ? (
        <div className={styles.center}>
          <Spinner />
        </div>
      ) : candidates.length === 0 ? (
        info?.enabled && (
          <EmptyState title={t('import.emptyTitle')} description={t('import.emptyDescription')} />
        )
      ) : (
        <>
          <div className={styles.selectBar}>
            <button type="button" className={styles.linkBtn} onClick={toggleAll}>
              {allSelected ? t('import.clearSelection') : t('import.selectAll')}
            </button>
            <button type="button" className={styles.linkBtn} onClick={() => setHideImported((v) => !v)}>
              {hideImported ? t('import.showImported') : t('import.hideImported')}
            </button>
            <span style={{ flex: 1 }} />
            <Button variant="outline" onClick={doImportAll} disabled={importing}>
              {total != null ? t('import.importAllCount', { count: total }) : t('import.importAll')}
            </Button>
            <Button onClick={doImport} loading={importing} disabled={!selected.size}>
              <Download size={15} strokeWidth={2} />
              {t('import.importSelected', { count: selected.size })}
            </Button>
          </div>

          <div className={styles.grid}>
            {visible.map((c) => {
              const media = c.gifUrl || c.imageUrl;
              const on = selected.has(keyOf(c));
              return (
                <button
                  type="button"
                  key={keyOf(c)}
                  className={`${styles.card} ${on ? styles.cardOn : ''} ${c.imported ? styles.cardImported : ''}`}
                  onClick={() => toggle(c)}
                >
                  <div className={styles.media}>
                    {media ? (
                      <img src={media} alt={c.name} loading="lazy" decoding="async" />
                    ) : (
                      <ImageIcon size={22} strokeWidth={1.5} />
                    )}
                    {c.imported && !on && (
                      <span className={styles.importedTag}>{t('import.importedBadge')}</span>
                    )}
                    {on && (
                      <span className={styles.check}>
                        <CheckCircle2 size={20} strokeWidth={2.5} />
                      </span>
                    )}
                  </div>
                  <div className={styles.cardBody}>
                    <span className={styles.name}>{c.name}</span>
                    <span className={styles.meta}>
                      {[c.targetMuscles?.[0] || c.bodyParts?.[0], c.equipments?.[0]]
                        .filter(Boolean)
                        .join(' · ')}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>

          <div className={styles.pager}>
            <span className={styles.pagerCount}>
              {total != null
                ? t('import.showingCount', { shown: candidates.length, total })
                : t('import.shownCount', { count: candidates.length })}
            </span>
            {hasMore && (
              <Button variant="outline" onClick={loadMore} loading={loadingMore}>
                {t('import.loadMore')}
              </Button>
            )}
          </div>
        </>
      )}

      {/* Histórico */}
      {history.length > 0 && (
        <div className={styles.history}>
          <h3 className={styles.historyTitle}>{t('import.historyTitle')}</h3>
          <div className={styles.historyList}>
            {history.map((h) => (
              <div key={h.id} className={styles.historyRow}>
                <span className={styles.histSource}>{h.source}</span>
                <span className={styles.histQuery}>{h.query || '—'}</span>
                <span>
                  {t('import.histCreated', { count: h.created })} · {t('import.histUpdated', { count: h.updated })}
                  {h.enriched ? ` · ${t('import.histEnriched', { count: h.enriched })}` : ''}
                  {h.failed ? ` · ${t('import.histFailed', { count: h.failed })}` : ''}
                </span>
                <span className={styles.histDate}>
                  {new Date(h.createdAt).toLocaleString('pt-BR')}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
}
